import axios from 'axios';
import { useBoundStore } from '@Store/useBoundStore.ts';
import {
  ObservabilityErrorSeverity,
  reportError,
} from '@Services/observability/initObservability.ts';
import { HttpService } from '@Services/api/httpService.ts';
import { updateCustomerDataFromServer } from '@Services/init/util.ts';
import { identifyTrackingServiceUser } from '@Services/analytics/tracking.service.ts';
import { identifyLogRocketUser } from '@Services/analytics/logrocket.service.ts';
import { identifyGA4User, setGA4UserProperties } from '@Services/analytics/analytics.service.ts';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types.ts';
import { EDD_VERSION } from '@Pages/happyFlowPages/eddQuestions/consts.ts';
import { findMappedError } from '@Services/errors/errorsMap.ts';
import { ErrorType } from '@Services/errors/errorType.ts';
import type { IPartnerDataResponse } from '@Types/types.ts';
import type { OnboardingTypeEnum } from '@Store/slices/customer/customer.types';

export const handlePartnerFlowInitialData = async (token: string): Promise<void> => {
  if (!token) {
    //TODO: Verify nothing else is needed
    return;
  }

  try {
    const { data } = await HttpService.get(`v3/onboarding?token=${token}`);
    const partnerData = data as IPartnerDataResponse;

    if (partnerData.partnerId) {
      setupPartnerOnboardingData(partnerData);
    }

    if (partnerData.whiteGloveEligible) {
      useBoundStore.getState().onboardingSlice.setIsWhiteGloveFlow(true);
    }

    if (useBoundStore.getState().onboardingSlice.getIsEmbedded()) {
      await setupPasswordlessOnboardingData(token);
    }
  } catch (e) {
    if (axios.isAxiosError(e)) {
      if (e.code === 'ECONNABORTED') {
        return; // Don't report aborted requests (user canceled/navigated away)
      }
      const errorCode = e.response?.data?.errors?.[0]?.code;
      if (errorCode && findMappedError(errorCode)?.errorType === ErrorType.BUSINESS_LOGIC) {
        return; // Do not send business logic errors to Bugsnag
      }
    }

    const errorToReport = {
      metadata: {
        name: 'handlePartnerFlowInitialData',
        message: JSON.stringify(e),
      },
      severity: ObservabilityErrorSeverity.ERROR,
    };
    reportError(errorToReport);
  }
};

const setupPartnerOnboardingData = (partnerData: IPartnerDataResponse) => {
  const setIsPartnerFlow = useBoundStore.getState().onboardingSlice.setIsPartnerFlow;
  const setOnboardingIdentifier = useBoundStore.getState().onboardingSlice.setOnboardingIdentifier;
  const setPartnerLabels = useBoundStore.getState().onboardingSlice.setPartnerLabels;
  const customerData = useBoundStore.getState().customer;
  const setPartnerName = useBoundStore.getState().onboardingSlice.setPartnerName;
  const isForceEmbedded = import.meta.env.VITE_FORCE_EMBEDDED;
  const isInsideIframe = isForceEmbedded
    ? true
    : window.parent !== window && window.self !== window.top;

  setPartnerLabels(partnerData?.labels || []);
  setIsPartnerFlow(true);
  setPartnerName(partnerData.partnerName || '');
  setOnboardingIdentifier(
    isInsideIframe
      ? OnboardingFlowIdentifier.PARTNER_EMBEDDED
      : OnboardingFlowIdentifier.PARTNER_CONNECT
  );
  const customerDataToUpdate = { ...customerData };
  customerDataToUpdate.customerData.customerBusinessData.businessName =
    partnerData.partnerName || '';
  customerDataToUpdate.customerData.email = partnerData.email || '';
  customerDataToUpdate.customerData.firstName = partnerData.firstName || '';
  customerDataToUpdate.customerData.lastName = partnerData.lastName || '';
  customerDataToUpdate.customerLogoUrl = `https://assets.lili.co/connect/${partnerData.partnerId}/ob_logo.png`;
  customerDataToUpdate.partnerId = partnerData.partnerId;
  customerDataToUpdate.customerId = partnerData.customerId;
  customerDataToUpdate.campaignName = partnerData.campaignName;
  updateCustomerDataFromServer(customerDataToUpdate, customerData);
};

const setupPasswordlessOnboardingData = async (token: string) => {
  const getCustomerData = useBoundStore.getState().getCustomerData;
  const setCustomerData = useBoundStore.getState().setCustomerData;
  const setKybData = useBoundStore.getState().setKybData;

  const { data: onboardingPartnerData } = await HttpService.get(
    `v3/onboardingPartner?token=${token}&eddVersion=${EDD_VERSION}`
  );

  identifyTrackingServiceUser(onboardingPartnerData.customerId);
  identifyGA4User(onboardingPartnerData.customerId);
  let customerDataToUpdate = { ...getCustomerData() };
  customerDataToUpdate = {
    ...customerDataToUpdate,
    accessToken: onboardingPartnerData.accessToken,
    customerId: onboardingPartnerData.customerId,
    business: {
      status: onboardingPartnerData.business?.status,
      businessType: onboardingPartnerData.business?.businessType,
    },
    isNonUsSupported: onboardingPartnerData.nonUsSupported,
    onboardingStatus: onboardingPartnerData.onboardingStatus,
    didCompleteNonUSEDD: onboardingPartnerData.didCompleteNonUSEDD,
    hasSoleProprietor: onboardingPartnerData?.hasSoleProprietor || false,
    campaignName: onboardingPartnerData.campaignName,
  };

  customerDataToUpdate.customerData = {
    ...customerDataToUpdate.customerData,
    birthDate: onboardingPartnerData.birthDate,
    customerAddressData: {
      ...customerDataToUpdate?.customerData?.customerAddressData,
      ...onboardingPartnerData.customerAddressData,
    },
    registeredBusinessAddressData: onboardingPartnerData.registeredBusinessAddressData,
    businessAddressData: {
      ...customerDataToUpdate.customerData.businessAddressData,
      ...onboardingPartnerData.businessAddress,
    },
    email: onboardingPartnerData.email,
    firstName: onboardingPartnerData.firstName,
    lastName: onboardingPartnerData.lastName,
    phoneNumber: onboardingPartnerData.phoneNumber,
    ssn: onboardingPartnerData.ssn,
    lastStepData: onboardingPartnerData.lastStepData,
    onboardingTypeEnum: onboardingPartnerData.onboardingTypeEnum as OnboardingTypeEnum,
  };

  setKybData({ business: onboardingPartnerData.business });
  setCustomerData(customerDataToUpdate);
  identifyLogRocketUser(onboardingPartnerData.customerId, {
    onboardingStatus: onboardingPartnerData.onboardingStatus,
    isNonUsSupported: onboardingPartnerData.nonUsSupported,
  });
  setGA4UserProperties();
};
